import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, Server, Users, Loader2, Package, Boxes,
} from "lucide-react";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import SEO from "@/components/SEO";
import { SOFTWARE_APPS, PLATFORM, APP_ORDER, SYSTEM_REQUIREMENTS, IMPLEMENTATION_KIT } from "@/data/softwareApps";

const API = process.env.REACT_APP_BACKEND_URL;

function DemoRequestForm({ app }) {
  const [form, setForm] = useState({
    name: "", email: "", hospital_name: "", title: "", phone: "",
  });
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const field = "w-full rounded-lg bg-white border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none text-slate-900 text-sm px-4 py-3 placeholder:text-slate-400 transition-all";
  const label = "block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2";

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending"); setMsg("");
    try {
      const r = await fetch(`${API}/api/demo-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          product_interest: `${app.code} — ${app.expansion}`,
          source: "website",
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail || "Could not submit the request");
      setStatus("success");
    } catch (err) {
      setStatus("error"); setMsg(String(err.message));
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center"
        data-testid="demo-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h3 className="text-xl font-black text-slate-900 mb-2">Walkthrough requested.</h3>
        <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
          Your request for <strong>{app.code}</strong> is logged. We will confirm a time and send
          the agenda before the session, with the Techinquest engineering team on the call.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-7 lg:p-9 space-y-4"
      data-testid="demo-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="dm-name" className={label}>Your name *</label>
          <input id="dm-name" name="name" required value={form.name} onChange={onChange}
            className={field} placeholder="Jane Alvarez" data-testid="demo-form-name" />
        </div>
        <div>
          <label htmlFor="dm-email" className={label}>Work email *</label>
          <input id="dm-email" name="email" type="email" required value={form.email} onChange={onChange}
            className={field} placeholder="jalvarez@hospital.org" data-testid="demo-form-email" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="dm-hospital" className={label}>Health system</label>
          <input id="dm-hospital" name="hospital_name" value={form.hospital_name} onChange={onChange}
            className={field} placeholder="Memorial Health" data-testid="demo-form-hospital" />
        </div>
        <div>
          <label htmlFor="dm-title" className={label}>Title</label>
          <input id="dm-title" name="title" value={form.title} onChange={onChange}
            className={field} placeholder="VP Supply Chain" data-testid="demo-form-title" />
        </div>
      </div>
      <div>
        <label htmlFor="dm-phone" className={label}>Phone</label>
        <input id="dm-phone" name="phone" value={form.phone} onChange={onChange}
          className={field} placeholder="(281) 555-0134" data-testid="demo-form-phone" />
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">
          Application
        </p>
        <p className="text-sm font-bold text-slate-800" data-testid="demo-form-product">
          {app.code} — {app.expansion}
        </p>
      </div>
      {status === "error" && (
        <p className="text-sm text-rose-600" data-testid="demo-form-error">{msg}</p>
      )}
      <button type="submit" disabled={status === "sending"} data-testid="demo-form-submit"
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-white font-black text-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
        style={{ backgroundColor: app.accent }}>
        {status === "sending"
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
          : <>Book the {app.code} walkthrough <ArrowRight className="w-4 h-4" /></>}
      </button>
      <p className="text-[11px] text-slate-500 text-center">
        A scoping conversation, not a sales pitch. Licensing is quoted afterwards.
      </p>
    </form>
  );
}

const Reveal = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div className={className}
      initial={reduce ? false : { opacity: 0, y: 22 }}
      whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  );
};

export default function SoftwareAppPage() {
  const { slug } = useParams();
  const app = SOFTWARE_APPS[slug];
  const other = SOFTWARE_APPS[APP_ORDER.find((s) => s !== slug)];
  const [face, setFace] = useState("front");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    setFace("front");
  }, [slug]);

  if (!app) {
    return (
      <div className="min-h-screen bg-white">
        <PublicSiteHeader />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="app-not-found">
          <h1 className="text-3xl font-black text-slate-900 mb-4">Application not found</h1>
          <Link to="/solutions/heart" className="text-sky-700 font-bold underline">
            Back to the platform overview
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid={`app-page-${app.slug}`}>
      <SEO
        title={`${app.code} — ${app.expansion} | Sterile Mate × TechInquest`}
        description={`${app.tagline} ${app.summary.slice(0, 120)}`}
        canonical={`/solutions/heart/${app.slug}`}
      />
      <PublicSiteHeader />

      {/* hero */}
      <section className="relative overflow-hidden bg-[#050a16]">
        <div className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <Link to="/solutions/heart" data-testid="app-back-link"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-cyan-300 transition-colors mb-10">
            <ArrowLeft className="w-3.5 h-3.5" /> Platform overview
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div className="inline-flex flex-wrap items-center gap-3 mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em]"
                    style={{ color: app.accent }}>{app.badge}</span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                    · {app.edition}
                  </span>
                </div>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10
                  text-[10px] font-black uppercase tracking-[0.2em] text-slate-300">
                  <ShieldCheck className="w-3.5 h-3.5" /> Enterprise healthcare application
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05] mb-4"
                data-testid="app-hero-title">
                {app.code}
              </h1>
              <p className="text-base font-bold uppercase tracking-[0.18em] mb-6" style={{ color: app.accent }}>
                {app.expansion}
              </p>
              <p className="text-lg text-slate-200 leading-relaxed mb-5">{app.tagline}</p>
              <p className="text-sm text-slate-400 leading-relaxed mb-9 max-w-xl">{app.summary}</p>
              <div className="flex flex-wrap gap-4">
                <a href="#walkthrough" data-testid="app-cta-demo"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-white font-black text-sm transition-transform hover:-translate-y-0.5"
                  style={{ backgroundColor: app.accent }}>
                  Request a walkthrough <ArrowRight className="w-4 h-4" />
                </a>
                <Link to={`/solutions/heart/${other.slug}`} data-testid="app-cta-other"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 text-white text-sm font-bold transition-colors">
                  See {other.code} instead
                </Link>
              </div>

              <div className="mt-10 flex items-center gap-4" data-testid="app-builtby">
                <div className="rounded-lg bg-white px-4 py-2.5">
                  <img src={PLATFORM.builtBy.logo} alt="Techinquest"
                    data-testid="app-techinquest-logo" className="h-6 w-auto" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                    {PLATFORM.builtBy.role}
                  </p>
                  <p className="text-xs text-slate-400">{PLATFORM.builtBy.line}</p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <img src={face === "front" ? app.box : app.boxBack}
                alt={face === "front" ? app.boxAlt : app.boxBackAlt}
                data-testid="app-hero-box"
                className="w-full max-w-md mx-auto drop-shadow-[0_35px_60px_rgba(0,0,0,0.55)]" />
              <div className="flex items-center justify-center gap-2 mt-6" data-testid="box-face-toggle">
                {[["front", "Front"], ["back", "Back cover"]].map(([k, labelText]) => (
                  <button key={k} type="button" onClick={() => setFace(k)}
                    data-testid={`box-face-${k}`}
                    className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-colors ${
                      face === k
                        ? "bg-white text-slate-900"
                        : "bg-white/5 border border-white/15 text-slate-300 hover:bg-white/10"}`}>
                    {labelText}
                  </button>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* enterprise grade */}
      <section className="bg-[#070D1E] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <Reveal className="max-w-2xl mb-12">
            <p className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400 mb-4">
              Enterprise grade
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight">
              Built for a health system, not a department.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="app-enterprise">
            {PLATFORM.enterprise.map((e, i) => (
              <Reveal key={e.t} delay={Math.min(i * 0.07, 0.3)}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6"
                  data-testid={`app-enterprise-${i}`}>
                  <p className="text-sm font-black text-white mb-2">{e.t}</p>
                  <p className="text-sm text-slate-400 leading-relaxed">{e.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* features */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <Reveal className="max-w-2xl mb-12">
            <p className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400 mb-4">
              What is in the box
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">
              Top features
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="app-features">
            {app.topFeatures.map((f, i) => (
              <Reveal key={f.t} delay={Math.min(i * 0.06, 0.3)}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-xl hover:shadow-slate-900/5 transition-all"
                  data-testid={`app-feature-${i}`}>
                  <CheckCircle2 className="w-5 h-5 mb-4" style={{ color: app.accent }} />
                  <p className="text-base font-black text-slate-900 mb-2">{f.t}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* where it works — photographic story */}
      <section className="bg-[#070D1E] border-b border-white/10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <Reveal className="max-w-2xl mb-12">
            <p className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400 mb-4">
              Where it works
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mb-5">
              From the floor to the boardroom.
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed">
              {app.code} is not a finance tool bolted on at the end. It follows the same chain your
              people already work in — and closes the gap between what happens on the floor and what
              leadership is told.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5" data-testid="app-story">
            {app.story.map((s, i) => (
              <Reveal key={s.k} delay={Math.min(i * 0.08, 0.3)}>
                <figure className="group relative h-full rounded-2xl overflow-hidden border border-white/10"
                  data-testid={`app-story-${i}`}>
                  <img src={s.img} alt={s.alt} loading="lazy"
                    className="w-full h-64 sm:h-72 object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#04080f] via-[#04080f]/55 to-transparent" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-6">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2"
                      style={{ color: app.accent }}>{s.k}</p>
                    <p className="text-sm text-slate-100 leading-relaxed">{s.c}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* competence + who for */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <div className="flex items-center gap-2.5 mb-6">
              <ShieldCheck className="w-5 h-5" style={{ color: app.accent }} />
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">Competencies</p>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-6">
              What the system is expected to be good at.
            </h3>
            <ul className="space-y-3" data-testid="app-competencies">
              {app.competencies.map((c, i) => (
                <li key={c} className="flex gap-3 text-sm text-slate-700 leading-relaxed"
                  data-testid={`app-competency-${i}`}>
                  <span className="font-black tabular-nums" style={{ color: app.accent }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {c}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex items-center gap-2.5 mb-6">
              <Users className="w-5 h-5" style={{ color: app.accent }} />
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">Who it is for</p>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-6">
              The people who sign off on it.
            </h3>
            <div className="flex flex-wrap gap-2.5" data-testid="app-audience">
              {app.whoFor.map((w) => (
                <span key={w} className="px-4 py-2 rounded-full border border-slate-300 bg-white text-sm font-bold text-slate-700">
                  {w}
                </span>
              ))}
            </div>
            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-sm text-slate-600 leading-relaxed">
                Licensing is quoted per health system after a scoping call, based on facilities and
                modules. Ask for the walkthrough first — the numbers follow the scope, not the other
                way round.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* system requirements — printed on the back of the box */}
      <section className="bg-slate-100 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid lg:grid-cols-[minmax(0,380px)_1fr] gap-12 lg:gap-16 items-start">
            <Reveal>
              <img src={app.boxBack} alt={app.boxBackAlt}
                data-testid="app-box-back"
                className="w-full max-w-[380px] mx-auto drop-shadow-[0_25px_45px_rgba(15,23,42,0.3)]" />
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex items-center gap-2.5 mb-4">
                <Package className="w-5 h-5" style={{ color: app.accent }} />
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">
                  On the back of the box
                </p>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-8">
                Printed on the back
              </h2>
              <div className="space-y-4 mb-10" data-testid="app-box-back-copy">
                {app.printedOnBack.map((b, i) => (
                  <div key={b.h} className="rounded-2xl border border-slate-200 bg-white p-6"
                    data-testid={`app-back-claim-${i}`}>
                    <p className="text-sm font-black uppercase tracking-wider text-slate-900 mb-2">
                      {b.h}
                    </p>
                    <p className="text-sm text-slate-600 leading-relaxed">{b.d}</p>
                  </div>
                ))}
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight mb-5">
                System requirements
              </h3>
              <dl className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-200 overflow-hidden"
                data-testid="app-system-requirements">
                {SYSTEM_REQUIREMENTS.map((r, i) => (
                  <div key={r.k} className="grid sm:grid-cols-[160px_1fr] gap-1 sm:gap-5 px-5 py-4"
                    data-testid={`app-sysreq-${i}`}>
                    <dt className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500 pt-0.5">
                      {r.k}
                    </dt>
                    <dd className="text-sm text-slate-700 leading-relaxed">{r.v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </div>
      </section>

      {/* compatibility */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <Reveal className="max-w-2xl mb-12">
            <div className="flex items-center gap-2.5 mb-4">
              <Server className="w-5 h-5" style={{ color: app.accent }} />
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">Compatibility</p>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-5">
              It fits what you already run.
            </h2>
            <div className="flex flex-wrap gap-2" data-testid="app-erps">
              {PLATFORM.erps.map((e) => (
                <span key={e} className="px-3 py-1.5 rounded-md bg-slate-100 border border-slate-200 text-xs font-bold text-slate-600">
                  {e}
                </span>
              ))}
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="app-compatibility">
            {PLATFORM.compatibility.map((c, i) => (
              <Reveal key={c.t} delay={Math.min(i * 0.06, 0.3)}>
                <div className="h-full rounded-2xl border border-slate-200 bg-slate-50 p-6"
                  data-testid={`app-compat-${i}`}>
                  <p className="text-sm font-black text-slate-900 mb-2">{c.t}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* implementation kit */}
      <section className="bg-[#050a16] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <Reveal className="max-w-2xl mb-12">
            <div className="flex items-center gap-2.5 mb-4">
              <Boxes className="w-5 h-5" style={{ color: app.accent }} />
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">
                Implementation kit
              </p>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mb-5">
              What your team is handed at kickoff.
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed">{IMPLEMENTATION_KIT.intro}</p>
          </Reveal>

          <Reveal>
            <div className="rounded-2xl border p-7 mb-6"
              style={{ borderColor: `${app.accent}55`, backgroundColor: `${app.accent}12` }}
              data-testid="app-kit-first">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2"
                style={{ color: app.accent }}>
                First deliverable · {app.code}
              </p>
              <p className="text-lg font-black text-white mb-2">{app.kitFirst.t}</p>
              <p className="text-sm text-slate-300 leading-relaxed max-w-3xl">{app.kitFirst.d}</p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="app-kit">
            {IMPLEMENTATION_KIT.items.map((k, i) => (
              <Reveal key={k.n} delay={Math.min(i * 0.05, 0.3)}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6"
                  data-testid={`app-kit-${i}`}>
                  <p className="text-xs font-black tabular-nums mb-3" style={{ color: app.accent }}>
                    {k.n}
                  </p>
                  <p className="text-sm font-black text-white mb-2">{k.t}</p>
                  <p className="text-sm text-slate-400 leading-relaxed">{k.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* demo request */}
      <section id="walkthrough" className="bg-slate-50 border-t border-slate-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
            <Reveal className="lg:col-span-2">
              <p className="text-[11px] font-black uppercase tracking-[0.25em] mb-4"
                style={{ color: app.accent }}>
                Request a walkthrough
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight mb-5">
                See {app.code} against your own numbers.
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-8">
                A working session with the Techinquest engineering team, scoped to your facilities
                and the systems you already run. We will show the product, not slides.
              </p>
              <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 flex items-center gap-4">
                <img src={PLATFORM.builtBy.logo} alt="Techinquest" className="h-6 w-auto" />
                <p className="text-xs text-slate-500 leading-snug">{PLATFORM.builtBy.line}</p>
              </div>
            </Reveal>
            <div className="lg:col-span-3 w-full">
              <Reveal delay={0.1}>
                <DemoRequestForm app={app} />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* cross-sell */}
      <section className="bg-[#050a16]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <Reveal>
            <Link to={`/solutions/heart/${other.slug}`} data-testid="app-crosssell"
              className="group grid sm:grid-cols-[180px_1fr_auto] gap-8 items-center rounded-2xl border border-white/10 bg-white/5 hover:bg-white/[0.08] p-7 transition-colors">
              <img src={other.box} alt={other.boxAlt} className="w-full max-w-[180px] mx-auto" />
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: other.accent }}>
                  {other.badge}
                </p>
                <p className="text-xl font-black text-white mb-1.5">
                  {other.code} — {other.expansion}
                </p>
                <p className="text-sm text-slate-300 leading-relaxed max-w-xl">{other.tagline}</p>
              </div>
              <span className="inline-flex items-center gap-2 text-sm font-bold text-white group-hover:gap-3.5 transition-all">
                Open <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
